import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import BookingAppointment from './BookingAppointment';
import { isStudentEnrolled } from "../utils/enrollmentUtils";
import API_URL from '../apiConfig';

// Restore original calendar icon
const BookIcon = () => (
  <svg width="100%" height="100%" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="34" height="34" rx="4" fill="none"/>
    <path d="M11.3333 2.83337V7.08337M22.6667 2.83337V7.08337M29.75 19.3092V12.0417C29.75 7.79171 27.625 4.95837 22.6667 4.95837H11.3333C6.375 4.95837 4.25 7.79171 4.25 12.0417V24.0834C4.25 28.3334 6.375 31.1667 11.3333 31.1667H20.7967M29.75 19.3092C28.4892 18.3034 26.8883 17.7084 25.1458 17.7084C23.4033 17.7084 21.7742 18.3175 20.4992 19.3517C18.785 20.6975 17.7083 22.8084 17.7083 25.1459C17.7083 26.5342 18.105 27.8659 18.785 28.9709C19.3092 29.8351 19.9892 30.5859 20.7967 31.1667M29.75 19.3092C31.4783 20.6692 32.5833 22.78 32.5833 25.1459C32.5833 26.5342 32.1867 27.8659 31.5067 28.9709C31.11 29.6509 30.6142 30.2459 30.0333 30.7275C28.73 31.8892 27.03 32.5834 25.1458 32.5834C23.5167 32.5834 22.015 32.0592 20.7967 31.1667M9.91667 15.5834H18.4167M9.91667 22.6667H13.6283M25.1458 28.6875C25.1458 26.7325 26.7325 25.1459 28.6875 25.1459C26.7325 25.1459 25.1458 23.5592 25.1458 21.6042C25.1458 23.5592 23.5592 25.1459 21.6042 25.1459C23.5592 25.1459 25.1458 26.7325 25.1458 28.6875Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: { 
    opacity: 1, 
    scale: 1, 
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 30 }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: { duration: 0.2 }
  }
};

const BookingPopup = () => {
  const [showModal, setShowModal] = useState(false);
  const [teacherActive, setTeacherActive] = useState(null);
  const [BookIconClicked, setBookIconClicked] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [shouldRender, setShouldRender] = useState(false);

  // Normalize userRole to lowercase for consistent comparisons
  const rawUserRole = localStorage.getItem('userRole');
  const userRole = rawUserRole ? rawUserRole.toLowerCase() : '';
  const isEnrolled = localStorage.getItem('isEnrolled'); // "true" or "false"
  const location = useLocation();
  const email = localStorage.getItem('userEmail');

  // Add window resize listener - moved before any conditional returns
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // For teachers, fetch isActive from user_routes endpoint.
  useEffect(() => {
    // Check if component should render at all first
    const isSessionPage = location.pathname.includes('/session');
    const isValidRole = ['faculty', 'student'].includes(userRole);
    
    if (isSessionPage || !isValidRole) {
      setShouldRender(false);
      return;
    }
    
    if (userRole === 'student') {
      // Use function from enrollmentUtils.js to check enrollment
      const enrolled = isStudentEnrolled();
      console.log(`BookingPopup: Student enrollment check - ${enrolled}`);
      setShouldRender(enrolled);
      return;
    }
    
    if (userRole === 'faculty' && email) {
      fetch(`${API_URL}/user/get_user?email=${encodeURIComponent(email)}`)
        .then(res => res.json())
        .then(data => {
          setTeacherActive(data.isActive);
          setShouldRender(data.isActive === true);
        })
        .catch(err => {
          console.error("Error fetching user isActive:", err);
          setTeacherActive(false);
          setShouldRender(false);
        });
    }
  }, [userRole, email, isEnrolled, location.pathname]);

  // Add this effect to fetch enrollment status if not already set
  useEffect(() => {
    // If we're a student and enrollment status isn't set, fetch it
    if (userRole === 'student' && localStorage.getItem('isEnrolled') === null) {
      const studentId = localStorage.getItem('studentID') || localStorage.getItem('studentId');
      if (studentId) {
        console.log("BookingPopup: Fetching missing enrollment status");
        import("../utils/enrollmentUtils")
          .then(module => {
            module.fetchAndStoreEnrollmentStatus(studentId)
              .then(enrolled => {
                console.log("BookingPopup: Updated enrollment status -", enrolled);
                setShouldRender(enrolled);
              });
          });
      }
    }
  }, [userRole]);

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // If we shouldn't render the component, return null
  if (!shouldRender) {
    return null;
  }

  // Calculate button size and position based on screen size
  const buttonSize = windowWidth < 640 ? 'w-12 h-12' : 'w-14 h-14';
  const iconSize = windowWidth < 640 ? 'scale-75' : 'scale-100';

  return (
    <>
      {/* Floating Action Button - Now without fixed positioning */}
      <button
        onClick={() => {
          console.log("Booking button clicked!"); // Debug log
          setBookIconClicked(true);
          setTimeout(() => setBookIconClicked(false), 200) 
          setShowModal(true)
        }}
        className={`${buttonSize} rounded-full bg-[#397de2] hover:bg-[#54BEFF] 
                   flex items-center justify-center shadow-lg transform hover:scale-110 
                   transition-all duration-300 ease-in-out
                   ${BookIconClicked ? "scale-90" : "scale-100"}`}
        title="Book Appointment"
      >
        <div className={`w-8 h-8 ${iconSize}`}>
          <BookIcon />
        </div>
      </button>

      {/* Modal - Enhanced with modern design */}
      <AnimatePresence>
        {showModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[9999] p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-white/20 mx-2 sm:mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-[#397de2] px-4 sm:px-8 py-4 sm:py-6 flex justify-between items-center">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-white">
                      {userRole === 'faculty' ? 'Book Appointment' : 'Request Appointment'}
                    </h2>
                    <p className="text-blue-100 text-xs sm:text-sm hidden sm:block">
                      Schedule your consultation session
                    </p>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setShowModal(false)}
                  className="text-white hover:text-gray-200 transition-colors p-1 sm:p-2 rounded-lg hover:bg-white/20"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.button>
              </div>

              {/* Modal Content */}
              <div className="p-0">
                <BookingAppointment 
                  closeModal={handleCloseModal}
                  role={userRole}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BookingPopup;
